/**
 * GambleSession — core gamble flow orchestrator.
 *
 * Pure TS logic, no rendering dependencies.
 * Drives the gamble loop by calling the visual port for UI
 * and the request function for server communication.
 */

import type {
  GambleRequest,
  GambleResponse,
  GambleOfferDetails,
} from "@slot/client-engine";

import type { CanvasOverlayStore } from "../canvas-overlay/CanvasOverlayStore";
import type { GamblePresentationPort } from "./GamblePresentationPort";
import type { GambleFlowResult } from "./GambleTypes";
import type { Log } from "@slot/presentation";

export interface GambleSessionDeps {
  requestGamble: (req: GambleRequest) => Promise<GambleResponse>;
  presentationPort: GamblePresentationPort;
  /** The gamble panel is Pixi: the DOM chrome hides for the whole flow. */
  canvasOverlayStore: CanvasOverlayStore;
  log: Log;
  /**
   * Invoked when the player commits to a gamble pick (Red/Black etc.)
   * with the stake that is now being put at risk. The caller should
   * apply an optimistic balance deduction in response. Not invoked on
   * collect — collecting never stakes anything.
   */
  onStakeDeducted?: (stakeMinor: string) => void;
  /**
   * Invoked after a won pick's reveal when the reply carries an `accountBalanceSnapshot`. The caller
   * applies it to the balance store, so the next pick's stake deduction starts from the server's balance
   * instead of compounding on the last one. Nothing shows the store between picks: the bar, balance
   * included, is hidden for the whole flow (decision 18). Not invoked on a loss or a collect, which end
   * the flow with its `finalBalanceSnapshot`.
   */
  onBalanceSnapshot?: (snapshot: { balanceAmount: string }) => void;
  /**
   * Server-authoritative post-gamble settlement. Called exactly once
   * per flow at every exit path (collect, server-stop, lose, network
   * auto-collect). Mirrors `acknowledgePresentationComplete` for the
   * spin flow. Optional — providers that don't implement it leave the
   * session falling back to `latestBalance` from the last response.
   */
  acknowledgeGambleComplete?: (
    roundIdentifier: string
  ) => Promise<{ balanceAmount: string }>;
}

export interface GambleFlowInput {
  roundIdentifier: string;
  possibleClientActions: string[];
  /** The server's offer: its currentWinAmountMinor is the first pick's stake (decision 29). */
  gambleOfferDetails: GambleOfferDetails;
}

export class GambleSession {
  private readonly deps: GambleSessionDeps;

  public constructor(deps: GambleSessionDeps) {
    this.deps = deps;
  }

  /**
   * Run the interactive gamble loop.
   *
   * Flow:
   * 1. Present offer → player chooses gamble or collect
   * 2. If collect → notify server, exit
   * 3. If gamble → send to server → present result
   * 4. If win and more gambles allowed → loop
   * 5. If lose → exit
   */
  /** The gamble panel is Pixi art over the game: the DOM chrome hides for the whole flow. */
  public async runGambleFlow(input: GambleFlowInput): Promise<GambleFlowResult> {
    const releaseOverlay = this.deps.canvasOverlayStore.open();
    try {
      return await this.runGambleFlowUnderOverlay(input);
    } finally {
      releaseOverlay();
    }
  }

  private async runGambleFlowUnderOverlay(input: GambleFlowInput): Promise<GambleFlowResult> {
    const { roundIdentifier } = input;
    // Server-authoritative offer details: the spin reply's, then each chained reply's gambleOfferDetails, never
    // computed client-side.
    let offerDetails: GambleOfferDetails = input.gambleOfferDetails;
    // The stake: the offer's amount, then each won pick's newWinAmountMinor, the backend's pending win (decision 29).
    let currentWin = offerDetails.currentWinAmountMinor;
    let possibleActions = input.possibleClientActions;
    let gambleCount = 0;
    let latestBalance: { balanceAmount: string } | undefined;

    this.deps.log(
      `[GambleSession] start type=${offerDetails.gambleType} round=${roundIdentifier} win=${currentWin}`
    );

    while (true) {
      // Single server-driven exit condition: server stops offering "gamble".
      // `maxAttemptsRemaining` and `gambleCeilingMinor` on offerDetails are
      // UI-display values only — the server is authoritative for termination.
      if (!possibleActions.includes("gamble")) {
        this.deps.log(
          `[GambleSession] server no longer offers gamble, exiting`
        );
        break;
      }

      // Refresh currentWin on offerDetails (may have changed from last response)
      offerDetails = { ...offerDetails, currentWinAmountMinor: currentWin };

      // 1. Present offer and wait for player decision
      const choice = await this.deps.presentationPort.presentGambleOffer(offerDetails);

      if (choice.action === "collect") {
        this.deps.log(`[GambleSession] player chose collect at win=${currentWin}`);

        // Notify server of collect decision
        try {
          const collectResponse = await this.deps.requestGamble({
            roundIdentifier,
            gambleAction: "collect",
            stakedAmountMinor: currentWin,
          });
          if (collectResponse.accountBalanceSnapshot) {
            latestBalance = collectResponse.accountBalanceSnapshot;
          }
        } catch (e) {
          this.deps.log(
            `[GambleSession] collect request failed: ${e instanceof Error ? e.message : String(e)}`
          );
          // Collect failed — treat as collected anyway (server has the win)
        }

        break;
      }

      // 2. Player chose to gamble — commit optimistic stake deduction,
      //    then send to server.
      gambleCount++;
      this.deps.log(
        `[GambleSession] gamble #${gambleCount} option=${choice.optionId} currentWin=${currentWin}`
      );

      this.deps.onStakeDeducted?.(currentWin);

      let response: GambleResponse;
      try {
        response = await this.deps.requestGamble({
          roundIdentifier,
          gambleAction: "gamble",
          stakedAmountMinor: currentWin,
          gambleOptionId: choice.optionId,
        });
      } catch (e) {
        this.deps.log(
          `[GambleSession] gamble request failed: ${e instanceof Error ? e.message : String(e)}, auto-collecting`
        );
        // Network error during gamble — auto-collect at current win
        await this.deps.presentationPort.dismissGamble();
        const ackBalance = await this.settleGambleComplete(roundIdentifier);
        return {
          finalWinAmountMinor: currentWin,
          gambleCount,
          outcome: "collected",
          finalBalanceSnapshot: ackBalance ?? latestBalance,
        };
      }

      if (response.accountBalanceSnapshot) {
        latestBalance = response.accountBalanceSnapshot;
      }

      // 3. Present result
      if (response.gambleOutcome === "win") {
        await this.deps.presentationPort.presentGambleResult(response);
        // Applied before the next pick deducts its stake from the store (see onBalanceSnapshot).
        if (response.accountBalanceSnapshot) {
          this.deps.onBalanceSnapshot?.(response.accountBalanceSnapshot);
        }
        currentWin = response.newWinAmountMinor;
        possibleActions = response.possibleClientActions;

        // Refresh offer details from server-authoritative response.
        // Server decides attempts remaining, ceiling, and available options.
        if (response.gambleOfferDetails) {
          offerDetails = response.gambleOfferDetails;
        }

        this.deps.log(
          `[GambleSession] gamble won newWin=${currentWin} canGambleAgain=${possibleActions.includes("gamble")} serverAttemptsRemaining=${offerDetails.maxAttemptsRemaining ?? "n/a"}`
        );

        // Server-driven loop termination — next iteration guards handle it
      } else {
        // Lost
        await this.deps.presentationPort.presentGambleLost(response);
        currentWin = response.newWinAmountMinor;

        this.deps.log(`[GambleSession] gamble lost newWin=${currentWin}`);

        await this.deps.presentationPort.dismissGamble();
        const ackBalanceOnLose = await this.settleGambleComplete(roundIdentifier);
        return {
          finalWinAmountMinor: currentWin,
          gambleCount,
          outcome: "lost",
          finalBalanceSnapshot: ackBalanceOnLose ?? latestBalance,
        };
      }
    }

    // Collected (either by player choice, max attempts, or ceiling)
    await this.deps.presentationPort.dismissGamble();
    const ackBalance = await this.settleGambleComplete(roundIdentifier);

    return {
      finalWinAmountMinor: currentWin,
      gambleCount,
      outcome: "collected",
      finalBalanceSnapshot: ackBalance ?? latestBalance,
    };
  }

  /**
   * Server-authoritative settlement for the gamble phase. Mirrors the
   * `acknowledgePresentationComplete` step used for the spin flow.
   * Returns the authoritative post-gamble balance, or undefined if the
   * provider does not support the ack or the call fails (caller should
   * fall back to the last response's balance).
   */
  private async settleGambleComplete(
    roundIdentifier: string
  ): Promise<{ balanceAmount: string } | undefined> {
    if (!this.deps.acknowledgeGambleComplete) {
      return undefined;
    }

    try {
      const ackBalance = await this.deps.acknowledgeGambleComplete(roundIdentifier);
      return ackBalance;
    } catch (e) {
      this.deps.log(
        `[GambleSession] acknowledgeGambleComplete failed: ${e instanceof Error ? e.message : String(e)}`
      );
      return undefined;
    }
  }
}
